import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { postAiJob } from "@/utils/aiJobs";
import {
  BarChart3, TrendingUp, Gauge, Sparkles, Target, ShieldCheck,
  ArrowRight, Loader2, Check, X, AlertTriangle, Brain, FileText,
  Clock, MapPin, Euro, Percent, Copy, CheckCircle2, Lightbulb,
  Archive, Swords, Plus, Search, Tag, Trash2, Edit, Wand2, BookOpen, Trophy,
  Building2, BookMarked, Users, Signal, ClipboardCheck, PhoneCall, RefreshCw, Link2
} from "lucide-react";
import { toast } from "sonner";
import { ESTADO_LABELS, ESTADO_CLS, fmtEur, Field, Kpi } from "./shared";
import { CCAA_GROUPS } from "@/pages/tender_intelligence/ProvinceHeatMap";

const COMPETIDOR_COLORS = {
  fcc: "#f59e0b", urbaser: "#0ea5e9", sacyr: "#64748b", acciona: "#22c55e",
  prezero: "#16a34a", veolia: "#2563eb", suez: "#06b6d4", paprec: "#eab308",
  ohla: "#f97316", ferrovial: "#6366f1",
};

/** Ampliación 12/10/2026 · "Panel de alertas en la app" — mismos datos que
 * recibe el email semanal (`weekly_vigencia_alert_job`), mostrados aquí sin
 * esperar al lunes. Contratos de competidores trackeados que vencen pronto =
 * oportunidad de entrada para Valoriza. */
const VigenciaAlertsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verRevisadas, setVerRevisadas] = useState(false);
  const [marcando, setMarcando] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = verRevisadas ? `${API}/competitive-intel/vigencia-alertas/historico` : `${API}/competitive-intel/vigencia-alertas`;
      const r = await axios.get(url, verRevisadas ? {} : { params: { meses: 6, incluir_revisadas: false } });
      setData(r.data);
    } catch {
      toast.error("Error cargando alertas de vigencia");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [verRevisadas]);

  const marcarRevisada = async (externalId) => {
    setMarcando(externalId);
    try {
      await axios.post(`${API}/competitive-intel/vigencia-alertas/revisar`, { external_id: externalId });
      toast.success("Alerta marcada como revisada — no se repetirá en el email semanal");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al marcar como revisada");
    } finally {
      setMarcando(null);
    }
  };

  const diasRestantes = (fecha) => {
    const hoy = new Date();
    const d = new Date(fecha);
    return Math.max(0, Math.round((d - hoy) / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="vigencia-alertas-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
          <AlertTriangle size={13} /> Alertas de vigencia — contratos de competidores que vencen pronto
        </h3>
        <button
          onClick={() => setVerRevisadas((v) => !v)}
          data-testid="vigencia-alertas-toggle-historico"
          className={`text-[10px] px-2.5 py-1.5 rounded-lg border flex items-center gap-1 ${verRevisadas ? "bg-gray-900 text-white border-gray-900" : "bg-gray-50 text-gray-600 border-gray-200"}`}
        >
          <Archive size={11} /> {verRevisadas ? "Ver pendientes" : "Ver histórico revisadas"}
        </button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-400"><Loader2 className="animate-spin" size={12} /> Cargando…</div>
      ) : !data || data.items.length === 0 ? (
        <div className="text-xs text-gray-400 italic" data-testid="vigencia-alertas-empty">
          {verRevisadas
            ? "Aún no se ha marcado ninguna alerta como revisada."
            : "Ningún contrato de competidor trackeado vence en los próximos 6 meses con vigencia conocida (o ya están todas revisadas)."}
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="vigencia-alertas-lista">
          {data.items.map((it, i) => (
            <div key={i} className={`border rounded-lg p-2.5 text-xs flex items-start justify-between gap-2 ${verRevisadas ? "border-gray-100 bg-gray-50/40" : "border-amber-100 bg-amber-50/40"}`} data-testid={`vigencia-alerta-item-${i}`}>
              <div>
                <div className="font-semibold text-gray-900">{it.titulo}</div>
                <div className="text-gray-500 text-[11px] mt-0.5">
                  {it.municipio || it.organismo} {it.provincia ? `· ${it.provincia}` : ""} ·{" "}
                  <span className="font-semibold">{(it.competidor_slug || "").toUpperCase()}</span>
                </div>
                {verRevisadas && (
                  <div className="text-[10px] text-gray-400 mt-1">
                    Revisado por {it.revisado_por} el {it.revisado_en ? new Date(it.revisado_en).toLocaleDateString("es-ES") : "—"}
                    {it.nota && <span> · &ldquo;{it.nota}&rdquo;</span>}
                  </div>
                )}
              </div>
              <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                <div className="text-[11px] font-bold text-amber-700">{diasRestantes(it.vigencia_hasta_estimada)} días</div>
                <div className="text-[9px] text-gray-400">{it.vigencia_hasta_estimada}{it.vigencia_confirmada ? "" : " (estimada)"}</div>
                {!verRevisadas && (
                  <button
                    onClick={() => marcarRevisada(it.external_id)}
                    disabled={marcando === it.external_id}
                    data-testid={`vigencia-alerta-marcar-revisada-${i}`}
                    className="text-[9px] bg-gray-900 text-white px-1.5 py-0.5 rounded hover:bg-gray-700 disabled:opacity-40 flex items-center gap-1"
                  >
                    {marcando === it.external_id ? <Loader2 className="animate-spin" size={9} /> : <Check size={9} />} Revisada
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Ampliación 12/10/2026 · "Gráfico de Incumbentes" — mismo patrón "grid
 * CCAA, no geo real" que `ProvinceHeatMap.jsx` (ya en producción), pero
 * coloreado por el competidor DOMINANTE (más contratos) en cada provincia,
 * usando sólo los incumbentes ya verificados (PLACSP/TED/Euskadi). */
const IncumbentesZoneMap = () => {
  const [data, setData] = useState(null);
  const [registry, setRegistry] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filtroCompetidor, setFiltroCompetidor] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/competitive-intel/incumbentes/por-zona`),
      axios.get(`${API}/competitive-intel/competidores`),
    ])
      .then(([r1, r2]) => { setData(r1.data); setRegistry(r2.data.items || []); })
      .catch(() => toast.error("Error cargando mapa de incumbentes"))
      .finally(() => setLoading(false));
  }, []);

  const byProv = useMemo(() => {
    const m = {};
    (data?.items || []).forEach((i) => { m[i.provincia] = i; });
    return m;
  }, [data]);

  const competidoresConDominio = useMemo(() => {
    const slugs = new Set((data?.items || []).map((i) => i.dominante_slug));
    return registry.filter((c) => slugs.has(c.slug));
  }, [data, registry]);

  if (loading) return <div className="flex items-center gap-2 text-xs text-gray-400 bg-white rounded-xl border border-gray-200 p-4"><Loader2 className="animate-spin" size={12} /> Cargando mapa…</div>;
  if (!data || data.items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 text-xs text-gray-400 italic" data-testid="incumbentes-zone-map-empty">
        Aún no hay suficientes incumbentes con provincia identificada para construir el mapa de dominancia.
      </div>
    );
  }

  const selectedItem = selected ? byProv[selected] : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="incumbentes-zone-map">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
        <MapPin size={13} /> Quién domina cada zona — competidor con más contratos identificados por provincia
      </h3>

      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="zone-map-filtro-competidor">
        <button onClick={() => setFiltroCompetidor("")} data-testid="zone-map-filtro-todos"
          className={`flex-shrink-0 text-[11px] px-2.5 py-1 rounded-full border ${!filtroCompetidor ? "bg-[#007934] text-white border-[#007934]" : "bg-gray-50 text-gray-600 border-gray-200"}`}>
          Todos
        </button>
        {competidoresConDominio.map((c) => (
          <button key={c.slug} onClick={() => setFiltroCompetidor(c.slug)} data-testid={`zone-map-filtro-${c.slug}`}
            className={`flex-shrink-0 text-[11px] px-2.5 py-1 rounded-full border flex items-center gap-1 ${filtroCompetidor === c.slug ? "text-white border-transparent" : "bg-gray-50 text-gray-600 border-gray-200"}`}
            style={filtroCompetidor === c.slug ? { backgroundColor: COMPETIDOR_COLORS[c.slug] || "#007934" } : {}}>
            {c.nombre_display}
          </button>
        ))}
      </div>

      {selectedItem && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="zone-map-detail-card">
          <div className="flex items-center gap-2 text-xs">
            <b className="text-slate-800">{selected}</b>
            <span className="text-slate-500">
              {selectedItem.total_contratos} contratos · dominante <b>{selectedItem.dominante_nombre}</b> ({selectedItem.dominante_contratos})
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(selectedItem.por_competidor).sort((a, b) => b[1] - a[1]).map(([slug, n]) => (
              <span key={slug} className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: `${COMPETIDOR_COLORS[slug] || "#999"}22`, color: COMPETIDOR_COLORS[slug] || "#666" }}>
                {slug.toUpperCase()}: {n}
              </span>
            ))}
          </div>
          <button onClick={() => setSelected(null)} className="text-[11px] text-slate-400 hover:text-rose-600">✕</button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="incumbentes-zone-map-grid">
        {Object.entries(CCAA_GROUPS).map(([ccaa, provs]) => (
          <div key={ccaa} className="border border-slate-200 rounded-lg p-2 bg-slate-50/50">
            <div className="text-[10px] font-bold text-slate-500 uppercase mb-1 truncate">{ccaa}</div>
            <div className="flex flex-wrap gap-1">
              {provs.map((p) => {
                const item = byProv[p];
                const matchesFiltro = !filtroCompetidor || item?.dominante_slug === filtroCompetidor;
                const color = item && matchesFiltro ? (COMPETIDOR_COLORS[item.dominante_slug] || "#94a3b8") : null;
                const isActive = selected === p;
                return (
                  <button
                    key={p}
                    onClick={() => setSelected(isActive ? null : p)}
                    title={item ? `${p} · dominante: ${item.dominante_nombre} (${item.dominante_contratos}/${item.total_contratos})` : `${p} · sin incumbentes identificados`}
                    data-testid={`zone-tile-${p.replace(/\s+/g, "-")}`}
                    className={`text-[10px] font-semibold px-2 py-1 rounded border transition text-white ${isActive ? "ring-2 ring-offset-1 ring-indigo-500" : ""} ${!item || !matchesFiltro ? "bg-slate-50 text-slate-300 border-slate-100" : "border-transparent"}`}
                    style={item && matchesFiltro ? { backgroundColor: color } : {}}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-gray-400">
        {data.total_provincias} provincias con incumbentes identificados
        {data.sin_provincia_reconocida > 0 && ` · ${data.sin_provincia_reconocida} registros con provincia no reconocida (excluidos del mapa)`}.
        Grid por CCAA (no mapa geográfico real) — mismo patrón que Hit Rate por Zona.
      </p>
    </div>
  );
};

/** Entrega E (12/10/2026) · "quién tiene el contrato, desde cuándo, hasta
 * cuándo, con qué socios" — fuente verificable PLACSP, nunca un ganador
 * inventado (si PLACSP no ha publicado resultado, el contrato no aparece). */
const IncumbentesPanel = () => {
  const { user } = useAuth();
  const [registry, setRegistry] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [recalculando, setRecalculando] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/competitive-intel/competidores`),
        axios.get(`${API}/competitive-intel/incumbentes`, { params: filtro ? { competidor: filtro } : {} }),
      ]);
      setRegistry(r1.data.items || []);
      setItems(r2.data.items || []);
    } catch {
      toast.error("Error cargando incumbentes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filtro]);

  const recalcular = async () => {
    setRecalculando(true);
    try {
      const r = await axios.post(`${API}/competitive-intel/incumbentes/reconstruir`);
      toast.success(`Actualizado: ${r.data.total_en_bd_tras_upsert} contratos en el registro (${r.data.con_match_competidor_trackeado} nuevos/actualizados en esta pasada)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al recalcular");
    } finally {
      setRecalculando(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="incumbentes-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
          <Building2 size={13} /> Incumbentes verificados (PLACSP + TED + Euskadi) — quién tiene cada contrato
        </h3>
        {user?.role === "admin" && (
          <button
            onClick={recalcular}
            disabled={recalculando}
            data-testid="incumbentes-recalcular-btn"
            className="text-[10px] bg-gray-900 text-white px-2.5 py-1.5 rounded-lg hover:bg-gray-700 disabled:opacity-40 flex items-center gap-1"
          >
            {recalculando ? <Loader2 className="animate-spin" size={11} /> : <RefreshCw size={11} />} Recalcular (PLACSP + TED + Euskadi)
          </button>
        )}
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="incumbentes-filtro-competidor">
        <button onClick={() => setFiltro("")} data-testid="incumbentes-filtro-todos"
          className={`flex-shrink-0 text-[11px] px-2.5 py-1 rounded-full border ${!filtro ? "bg-[#007934] text-white border-[#007934]" : "bg-gray-50 text-gray-600 border-gray-200"}`}>
          Todos
        </button>
        {registry.map((c) => (
          <button key={c.slug} onClick={() => setFiltro(c.slug)} data-testid={`incumbentes-filtro-${c.slug}`}
            className={`flex-shrink-0 text-[11px] px-2.5 py-1 rounded-full border flex items-center gap-1 ${filtro === c.slug ? "bg-[#007934] text-white border-[#007934]" : "bg-gray-50 text-gray-600 border-gray-200"}`}>
            {c.nombre_display}
            {c.caveat && <AlertTriangle size={10} className={filtro === c.slug ? "text-white" : "text-amber-500"} />}
          </button>
        ))}
      </div>

      {filtro && registry.find((c) => c.slug === filtro)?.caveat && (
        <div className="flex items-start gap-1.5 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-2.5 py-2" data-testid="incumbentes-caveat">
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
          <span>{registry.find((c) => c.slug === filtro).caveat}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-400"><Loader2 className="animate-spin" size={12} /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-gray-400 italic" data-testid="incumbentes-empty">
          Aún sin contratos identificados para competidores trackeados. PLACSP/TED sólo publican el resultado una vez
          formalizado el contrato — si acaba de sincronizarse, puede no haber coincidencias todavía.
          {user?.role === "admin" && " Pulsa \"Recalcular (PLACSP + TED + Euskadi)\" tras una sincronización, o espera al refresco automático semanal (domingos)."}
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="incumbentes-lista">
          {items.map((it, i) => (
            <div key={i} className="border border-gray-100 rounded-lg p-2.5 text-xs" data-testid={`incumbente-item-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-gray-900">{it.titulo}</div>
                <span className="text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5 flex-shrink-0 font-semibold">
                  Fuente: {(it.fuente || "placsp").toUpperCase()}
                </span>
              </div>
              <div className="text-gray-500 text-[11px] mt-0.5">
                {it.municipio || it.organismo} {it.provincia ? `· ${it.provincia}` : ""}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-gray-600">
                <span><span className="font-semibold text-gray-700">Importe:</span> {it.importe_adjudicacion_eur ? fmtEur(it.importe_adjudicacion_eur) : "N/D"}</span>
                <span><span className="font-semibold text-gray-700">Adjudicado:</span> {it.fecha_adjudicacion || "N/D"}</span>
                <span>
                  <span className="font-semibold text-gray-700">Vigencia hasta:</span>{" "}
                  {it.vigencia_hasta_estimada ? `${it.vigencia_hasta_estimada}${it.vigencia_confirmada ? "" : " (estimada)"}` : "N/D (sin duración publicada)"}
                </span>
                {it.socios_ute_detectados?.length > 0 && (
                  <span className="flex items-center gap-1"><Link2 size={10} /> UTE con: {it.socios_ute_detectados.join(", ")}</span>
                )}
              </div>
              <div className="text-[10px] text-gray-400 mt-1 truncate">Ganador PLACSP (texto fuente): &ldquo;{it.ganador_raw}&rdquo;</div>
              {it.historial_versiones?.length > 0 && (
                <details className="mt-1" data-testid={`incumbente-historial-${i}`}>
                  <summary className="text-[10px] text-amber-600 cursor-pointer font-semibold">Rectificado {it.historial_versiones.length} {it.historial_versiones.length === 1 ? "vez" : "veces"} — ver historial</summary>
                  <div className="mt-1 space-y-1 pl-2 border-l-2 border-amber-200">
                    {it.historial_versiones.map((h, hi) => (
                      <div key={hi} className="text-[10px] text-gray-500">
                        Versión anterior (reemplazada {h.reemplazado_en?.slice(0, 10)}): importe {h.importe_adjudicacion_eur ? fmtEur(h.importe_adjudicacion_eur) : "N/D"} · vigencia hasta {h.vigencia_hasta_estimada || "N/D"}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {it.fuente_url && (
                <a href={it.fuente_url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline" data-testid={`incumbente-fuente-${i}`}>
                  Ver anuncio PLACSP original
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ConciliacionExpedientesPanel = () => {
  const { user } = useAuth();
  const [candidatos, setCandidatos] = useState([]);
  const [vinculos, setVinculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verVinculos, setVerVinculos] = useState(false);
  const [procesando, setProcesando] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [rc, rv] = await Promise.all([
        axios.get(`${API}/competitive-intel/conciliacion/candidatos`),
        axios.get(`${API}/competitive-intel/conciliacion/vinculos`),
      ]);
      setCandidatos(rc.data.candidatos || []);
      setVinculos(rv.data.vinculos || []);
    } catch {
      toast.error("Error cargando conciliación de expedientes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const decidir = async (c, accion) => {
    const key = c.par_id;
    setProcesando(key);
    try {
      const endpoint = accion === "confirmar" ? "confirmar" : "rechazar";
      await axios.post(`${API}/competitive-intel/conciliacion/${endpoint}`, {
        external_id_a: c.doc_a.external_id,
        external_id_b: c.doc_b.external_id,
        motivo: accion === "rechazar" ? "No es el mismo expediente real" : null,
      });
      toast.success(accion === "confirmar" ? "Vinculado como mismo expediente" : "Marcado como expedientes distintos");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al procesar la decisión");
    } finally {
      setProcesando("");
    }
  };

  const puedeDecidir = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="conciliacion-expedientes-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
          <Link2 size={13} /> Conciliación de expedientes entre fuentes (PLACSP · TED · Euskadi)
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setVerVinculos(v => !v)} data-testid="conciliacion-toggle-vinculos"
            className="text-[10px] bg-gray-50 text-gray-600 border border-gray-200 px-2.5 py-1.5 rounded-lg hover:bg-gray-100">
            {verVinculos ? "Ver candidatos pendientes" : `Ver confirmados (${vinculos.length})`}
          </button>
          <button onClick={load} data-testid="conciliacion-refrescar-btn"
            className="text-[10px] bg-gray-900 text-white px-2.5 py-1.5 rounded-lg hover:bg-gray-700 flex items-center gap-1">
            <RefreshCw size={11} /> Refrescar
          </button>
        </div>
      </div>

      <div className="flex items-start gap-1.5 text-[11px] bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-2.5 py-2">
        <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
        <span>
          Nunca se fusiona nada automáticamente. Abajo sólo se sugieren pares de registros de fuentes distintas
          que podrían ser el mismo expediente real (organismo, importe, fechas y CPV compatibles) con la razón
          exacta de por qué se sugieren. Una persona debe confirmar o rechazar cada par — ningún documento
          original se borra ni se modifica en ningún caso.
        </span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-400"><Loader2 className="animate-spin" size={12} /> Cargando…</div>
      ) : verVinculos ? (
        vinculos.length === 0 ? (
          <div className="text-xs text-gray-400 italic" data-testid="conciliacion-vinculos-empty">
            Aún no hay expedientes conciliados entre fuentes.
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="conciliacion-vinculos-lista">
            {vinculos.map((v, i) => (
              <div key={v.id || i} className="border border-emerald-100 bg-emerald-50/40 rounded-lg p-2.5 text-xs" data-testid={`conciliacion-vinculo-${i}`}>
                <div className="flex items-center gap-1.5 text-emerald-700 font-semibold mb-1">
                  <Check size={12} /> Mismo expediente confirmado
                </div>
                <div className="text-[10px] text-gray-500 font-mono break-all">{(v.external_ids || []).join("  ↔  ")}</div>
                <div className="text-[10px] text-gray-400 mt-1">
                  Confirmado por {v.confirmado_por || "N/D"} · {v.confirmado_en ? new Date(v.confirmado_en).toLocaleString("es-ES") : "N/D"}
                </div>
              </div>
            ))}
          </div>
        )
      ) : candidatos.length === 0 ? (
        <div className="text-xs text-gray-400 italic" data-testid="conciliacion-candidatos-empty">
          No hay candidatos pendientes de conciliar en este momento.
        </div>
      ) : (
        <div className="space-y-2 max-h-[32rem] overflow-y-auto" data-testid="conciliacion-candidatos-lista">
          {candidatos.map((c, i) => (
            <div key={c.par_id || i} className="border border-amber-200 bg-amber-50/30 rounded-lg p-2.5 text-xs space-y-1.5" data-testid={`conciliacion-candidato-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[c.doc_a, c.doc_b].map((d, di) => (
                  <div key={di} className="bg-white border border-gray-100 rounded p-2">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[9px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-semibold uppercase">{d.source}</span>
                      {d.url && (
                        <a href={d.url} target="_blank" rel="noreferrer" className="text-[9px] text-blue-600 hover:underline" data-testid={`conciliacion-candidato-${i}-url-${di}`}>
                          Ver original
                        </a>
                      )}
                    </div>
                    <div className="font-semibold text-gray-900 text-[11px] mt-0.5 line-clamp-2">{d.title}</div>
                    <div className="text-gray-500 text-[10px] mt-0.5">{d.organism}</div>
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1 text-[10px] text-gray-500">
                      <span>{d.value_eur ? fmtEur(d.value_eur) : "Importe N/D"}</span>
                      <span>Publicado: {d.publication_date || "N/D"}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-1">
                {(c.razones || []).map((r, ri) => (
                  <span key={ri} className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">{r}</span>
                ))}
              </div>
              {puedeDecidir && (
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => decidir(c, "confirmar")} disabled={procesando === c.par_id}
                    data-testid={`conciliacion-confirmar-${i}`}
                    className="text-[10px] bg-[#007934] text-white px-2.5 py-1.5 rounded-lg hover:bg-[#006528] disabled:opacity-40 flex items-center gap-1">
                    {procesando === c.par_id ? <Loader2 className="animate-spin" size={10} /> : <Check size={10} />} Es el mismo expediente
                  </button>
                  <button onClick={() => decidir(c, "rechazar")} disabled={procesando === c.par_id}
                    data-testid={`conciliacion-rechazar-${i}`}
                    className="text-[10px] bg-white text-gray-600 border border-gray-200 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1">
                    <X size={10} /> No es el mismo
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function CompetitiveIntel() {
  const [data, setData] = useState(null);
  const [loadingGen, setLoadingGen] = useState("");

  const load = async () => {
    try { const r = await axios.get(`${API}/tender360/competitive-intelligence`); setData(r.data); }
    catch { toast.error("Error cargando competitive intel"); }
  };
  useEffect(() => { load(); }, []);

  const regenerate = async (name) => {
    setLoadingGen(name);
    try { await postAiJob(`${API}/tender360/competitors/${name}/generate-profile`); toast.success("Perfil regenerado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message || "Error"); }
    finally { setLoadingGen(""); }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  const maxWins = Math.max(...data.competitors.map(c => c.wins), 1);

  return (
    <div className="space-y-4" data-testid="competitive-intel">
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Competidores en seguimiento" val={data.competitors.length}/>
        <Kpi label="Pliegos históricos rastreados" val={data.total_historic_tenders}/>
        <Kpi label="Tipos de contrato observados"  val={data.doc_types.length}/>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 overflow-x-auto">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Heatmap · Victorias por competidor y tipo de contrato</h3>
        {data.doc_types.length === 0
          ? <div className="text-xs text-gray-400">Aún no hay datos de victorias para construir el heatmap. Añade pliegos históricos en M4.</div>
          : (
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left p-2 text-[10px] uppercase text-gray-500">Competidor</th>
                  {data.doc_types.map(d => <th key={d} className="text-center p-2 text-[10px] uppercase text-gray-500">{d}</th>)}
                  <th className="text-right p-2 text-[10px] uppercase text-gray-500">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.competitors.map(c => (
                  <tr key={c.name} className="border-t border-gray-100">
                    <td className="p-2 font-semibold">{c.name}</td>
                    {data.doc_types.map(d => {
                      const v = (c.by_doc_type || {})[d] || 0;
                      const intensity = maxWins > 0 ? Math.min(1, v / maxWins) : 0;
                      const bg = v > 0 ? `rgba(239, 68, 68, ${0.15 + intensity * 0.7})` : "transparent";
                      return <td key={d} className="text-center p-2 font-mono font-bold" style={{backgroundColor: bg}}>{v || "—"}</td>;
                    })}
                    <td className="text-right p-2 font-mono font-bold text-gray-900">{c.wins}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Win rate por competidor (pliegos rastreados)</h3>
        {data.competitors.map(c => (
          <div key={c.name}>
            <div className="flex items-center justify-between text-[11px] mb-0.5">
              <span className="font-semibold">{c.name}</span>
              <span className="text-gray-500">
                {c.wins}/{c.participations} pliegos · <span className="font-bold text-gray-900">{c.win_rate}%</span>
                {c.vs_valoriza.head_to_head > 0 && (
                  <span className="ml-2 text-[10px] bg-[#007934]/10 text-[#007934] px-2 py-0.5 rounded font-semibold">
                    Valoriza {c.vs_valoriza.vsm_wins}-{c.vs_valoriza.their_wins} {c.name}
                  </span>
                )}
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div className="bg-rose-500 h-full" style={{ width: `${Math.min(100, c.win_rate)}%` }}/>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.competitors.map(c => (
          <div key={c.name} className="bg-white rounded-xl border border-gray-200 p-4 space-y-2" data-testid={`comp-card-${c.name}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-bold text-gray-900">{c.name}</div>
                <div className="text-[10px] text-gray-500">
                  {c.wins} victorias · {c.win_rate}% win rate ·
                  {c.vs_valoriza.head_to_head > 0 ? ` H2H Valoriza ${c.vs_valoriza.vsm_win_rate_pct}%` : " sin H2H"}
                </div>
              </div>
              <button onClick={()=>regenerate(c.name)} disabled={loadingGen === c.name}
                      className="text-[10px] bg-[#007934] text-white px-2 py-1 rounded hover:bg-[#006528] disabled:opacity-40 flex items-center gap-1"
                      data-testid={`comp-regen-${c.name}`}>
                {loadingGen === c.name ? <Loader2 className="animate-spin" size={10}/> : <Wand2 size={10}/>}
                Regenerar Pando
              </button>
            </div>
            {c.profile ? (
              <>
                {/* R05 · todo perfil generado por IA es conocimiento general sin
                    fuente ni fecha verificable — se marca claramente salvo que un
                    humano lo haya editado con fuente (verificado_manualmente). */}
                {c.profile.verificado_manualmente ? (
                  <div className="flex items-center gap-1.5 text-[10px] bg-emerald-50 border border-emerald-200 text-emerald-700 rounded px-2 py-1" data-testid={`comp-verified-${c.name}`}>
                    <ShieldCheck size={11}/> Verificado manualmente
                    {c.profile.fuente_manual && <span className="text-emerald-600">· {c.profile.fuente_manual}</span>}
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-[10px] bg-amber-50 border border-amber-200 text-amber-700 rounded px-2 py-1" data-testid={`comp-unverified-${c.name}`}>
                    <AlertTriangle size={11}/> IA · sin verificar — conocimiento general, no citar como hecho contrastado
                  </div>
                )}
                {c.profile.updated_at && (
                  <div className="text-[9px] text-gray-400">Generado/actualizado: {new Date(c.profile.updated_at).toLocaleDateString("es-ES")}</div>
                )}
                <p className="text-xs text-gray-700">{c.profile.perfil_general}</p>
                {c.profile.grupo_propietario && (
                  <div className="flex items-center gap-1.5 text-[10px] bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid={`comp-owner-${c.name}`}>
                    <span className="font-bold text-slate-600 uppercase">Grupo propietario:</span>
                    <span className="text-slate-700">{c.profile.grupo_propietario}</span>
                    {c.profile.grupo_propietario_verificado ? (
                      <span className="text-emerald-600 font-semibold">✓ verificado</span>
                    ) : (
                      <span className="text-amber-600 font-semibold">a verificar</span>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  <div>
                    <div className="font-bold text-green-700 uppercase mb-0.5">Fortalezas</div>
                    <ul className="list-disc pl-3 text-gray-700 space-y-0.5">{(c.profile.fortalezas || []).slice(0,3).map((f,i)=><li key={i}>{f}</li>)}</ul>
                  </div>
                  <div>
                    <div className="font-bold text-rose-700 uppercase mb-0.5">Debilidades</div>
                    <ul className="list-disc pl-3 text-gray-700 space-y-0.5">{(c.profile.debilidades || []).slice(0,3).map((f,i)=><li key={i}>{f}</li>)}</ul>
                  </div>
                </div>
                {c.profile.como_batirle && (
                  <div>
                    <div className="text-[10px] font-bold text-[#007934] uppercase mt-1 mb-0.5">Cómo batirle</div>
                    <ul className="list-disc pl-3 text-[10px] text-gray-700 space-y-0.5">{c.profile.como_batirle.slice(0,3).map((f,i)=><li key={i}>{f}</li>)}</ul>
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-1">
                  {c.profile.tipico_posicionamiento_precio && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-semibold">Precio: {c.profile.tipico_posicionamiento_precio}</span>}
                  {(c.profile.regiones_fuertes || []).slice(0,3).map((r,i)=><span key={i} className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{r}</span>)}
                </div>
              </>

            ) : (
              <p className="text-xs text-gray-400 italic">Sin perfil cargado — clica &ldquo;Regenerar Pando&rdquo;</p>
            )}
          </div>
        ))}
      </div>

      <ConciliacionExpedientesPanel />
      <IncumbentesPanel />
      <IncumbentesZoneMap />
      <VigenciaAlertsPanel />
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
// M3: CRM Pre-Tender Engagement — 360º Accounts
// ═══════════════════════════════════════════════════════════════════════════
